import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NewsCard from '../components/Home/NewsCard';
import { useApp } from '../context/AppContext';
import api from '../services/api';

const ArticlePage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { articles, showToast } = useApp();
    const [article, setArticle] = useState(null);
    const [relatedArticles, setRelatedArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const viewTrackedRef = useRef(false);

    useEffect(() => {
        viewTrackedRef.current = false;
        if (slug) {
            fetchArticle();
        }
        window.scrollTo(0, 0);
    }, [slug]);

    const fetchArticle = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/articles/${slug}`);
            setArticle(res.data);

            // ✅ FIX: Extract the articles array from the response
            const relatedRes = await api.get(`/articles?category=${res.data.category}&limit=4`);

            // Get the articles array - handle both response formats
            let relatedArticlesData = relatedRes.data;
            if (relatedRes.data.articles) {
                relatedArticlesData = relatedRes.data.articles;
            }

            // Make sure it's an array
            const filtered = Array.isArray(relatedArticlesData)
                ? relatedArticlesData.filter(a => a._id !== res.data._id)
                : [];

            setRelatedArticles(filtered.slice(0, 4));

            if (!viewTrackedRef.current && res.data) {
                await trackView(res.data);
            }

        } catch (error) {
            console.error('Error fetching article:', error);
            showToast('Article not found', 'error');
            navigate('/');
        } finally {
            setLoading(false);
        }
    };

    // Fixed: Use article's _id, not undefined 'id'
    const trackView = async (articleData) => {
        if (viewTrackedRef.current) return;

        const articleId = articleData._id;
        const storageKey = `article_viewed_${articleId}`;

        if (localStorage.getItem(storageKey)) {
            viewTrackedRef.current = true;
            return;
        }

        const sessionKey = `article_session_${articleId}`;
        if (sessionStorage.getItem(sessionKey)) {
            viewTrackedRef.current = true;
            return;
        }

        try {
            viewTrackedRef.current = true;
            sessionStorage.setItem(sessionKey, 'true');
            localStorage.setItem(storageKey, Date.now().toString());
            const res = await api.post(`/articles/${articleId}/view`);
            setArticle(prev => ({ ...prev, views: res.data.views }));
            console.log('✅ Article view counted!');
        } catch (error) {
            console.error('Error tracking view:', error);
        }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        showToast('Link copied to clipboard!');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Recently';
        const date = new Date(dateString);
        const now = new Date();
        const diffDays = Math.ceil((now - date) / (1000 * 60 * 60 * 24));
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
        return `${Math.floor(diffDays / 365)} years ago`;
    };

    const stripHtml = (html) => {
        if (!html) return '';
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || '';
    };

    const generateArticleSchema = () => {
        if (!article) return null;
        return {
            "@context": "https://schema.org",
            "@type": article.seo?.schemaType || "NewsArticle",
            "headline": article.seo?.metaTitle || article.title,
            "description": article.seo?.metaDescription || article.excerpt || stripHtml(article.content || '').substring(0, 200),
            "image": article.img || article.imgData,
            "datePublished": article.date || article.createdAt,
            "dateModified": article.updatedAt || article.createdAt,
            "author": { "@type": "Organization", "name": article.author || "Wistar News" },
            "publisher": { "@type": "Organization", "name": "Wistar News", "logo": { "@type": "ImageObject", "url": "https://wistarnews.com/logo.png" } },
            "mainEntityOfPage": { "@type": "WebPage", "@id": window.location.href },
            "keywords": article.tags || article.seo?.metaKeywords || article.category,
            "articleSection": article.category,
            "interactionStatistic": { "@type": "InteractionCounter", "interactionType": "https://schema.org/WatchAction", "userInteractionCount": article.views || 0 }
        };
    };

    if (loading) {
        return (
            <div className="loader" style={{ minHeight: '60vh' }}>
                <div className="loader-spinner"></div>
            </div>
        );
    }

    if (!article) {
        return (
            <div className="page" style={{ textAlign: 'center', padding: '60px 20px' }}>
                <h2>📰 Article Not Found</h2>
                <p>The article you're looking for doesn't exist or has been removed.</p>
                <button className="btn btn-red" onClick={() => navigate('/')} style={{ marginTop: '20px' }}>
                    Back to Home →
                </button>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{article.seo?.metaTitle || article.title} | Wistar News</title>
                <meta name="description" content={article.seo?.metaDescription || article.excerpt || stripHtml(article.content || '').substring(0, 160)} />
                <meta name="keywords" content={article.seo?.metaKeywords || article.tags || article.category} />
                <link rel="canonical" href={`https://wistarnews.com/article/${article.slug || article._id}`} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={article.seo?.ogTitle || article.seo?.metaTitle || article.title} />
                <meta property="og:description" content={article.seo?.ogDescription || article.seo?.metaDescription || article.excerpt || stripHtml(article.content || '').substring(0, 160)} />
                <meta property="og:image" content={article.seo?.ogImage || article.img || article.imgData} />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:site_name" content="Wistar News" />
                <meta property="article:published_time" content={article.date || article.createdAt} />
                <meta property="article:author" content={article.author} />
                <meta property="article:section" content={article.category} />
                <meta property="article:tag" content={article.tags} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={article.seo?.twitterTitle || article.seo?.metaTitle || article.title} />
                <meta name="twitter:description" content={article.seo?.twitterDescription || article.seo?.metaDescription || article.excerpt || stripHtml(article.content || '').substring(0, 160)} />
                <meta name="twitter:image" content={article.seo?.twitterImage || article.img || article.imgData} />
                <script type="application/ld+json">
                    {JSON.stringify(generateArticleSchema())}
                </script>
            </Helmet>

            <div className="page" style={{ paddingTop: '20px' }}>
                <button
                    onClick={() => navigate('/')}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: 'var(--navy)',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px'
                    }}
                >
                    ← Back to Home
                </button>

                <div className="two-col">
                    <div className="article-body" style={{ maxWidth: '760px' }}>
                        <div className="tag">{article.category}</div>
                        <h1 style={{ fontFamily: 'var(--ff-head)', fontSize: '32px', lineHeight: '1.3', margin: '16px 0 10px' }}>
                            {article.title}
                        </h1>

                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '20px',
                            paddingBottom: '16px',
                            borderBottom: '1px solid var(--lgray)',
                            marginBottom: '16px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>✍️</span>
                                <span style={{ fontSize: '14px', color: '#666' }}>{article.author}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>📅</span>
                                <span style={{ fontSize: '14px', color: '#666' }}>{formatDate(article.date)}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>📌</span>
                                <span style={{ fontSize: '14px', color: '#666' }}>{article.source}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>👁️</span>
                                <span style={{ fontSize: '14px', color: '#666', fontWeight: '600' }}>
                                    {article.views?.toLocaleString() || 0} views
                                </span>
                            </div>
                        </div>

                        <div className="article-hero">
                            <img
                                src={article.img || article.imgData || 'https://via.placeholder.com/800x400'}
                                alt={article.title}
                                style={{ width: '100%', height: '400px', objectFit: 'cover', borderRadius: '3px' }}
                                onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/800x400?text=No+Image';
                                }}
                            />
                        </div>

                        <div className="share-bar" style={{ display: 'flex', gap: '10px', margin: '20px 0', flexWrap: 'wrap' }}>
                            <button
                                className="share-btn"
                                style={{ background: '#1877f2', padding: '8px 16px', borderRadius: '3px', fontSize: '13px', fontWeight: '600', color: '#fff', cursor: 'pointer', border: 'none' }}
                                onClick={() => window.open(`https://facebook.com/sharer/sharer.php?u=${window.location.href}`)}
                            >
                                📘 Facebook
                            </button>
                            <button
                                className="share-btn"
                                style={{ background: '#000', padding: '8px 16px', borderRadius: '3px', fontSize: '13px', fontWeight: '600', color: '#fff', cursor: 'pointer', border: 'none' }}
                                onClick={() => window.open(`https://twitter.com/intent/tweet?text=${article.title}&url=${window.location.href}`)}
                            >
                                𝕏 Twitter
                            </button>
                            <button
                                className="share-btn"
                                style={{ background: '#25d366', padding: '8px 16px', borderRadius: '3px', fontSize: '13px', fontWeight: '600', color: '#fff', cursor: 'pointer', border: 'none' }}
                                onClick={() => window.open(`https://wa.me/?text=${article.title} ${window.location.href}`)}
                            >
                                💬 WhatsApp
                            </button>
                            <button
                                className="share-btn"
                                style={{ background: 'var(--red)', padding: '8px 16px', borderRadius: '3px', fontSize: '13px', fontWeight: '600', color: '#fff', cursor: 'pointer', border: 'none' }}
                                onClick={copyLink}
                            >
                                🔗 Copy Link
                            </button>
                        </div>

                        <div className="article-content" style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '16px', color: '#333' }}
                            dangerouslySetInnerHTML={{ __html: article.content || article.excerpt }}
                        />

                        {relatedArticles.length > 0 && (
                            <div style={{ marginTop: '40px' }}>
                                <h2 className="section-title">📰 Related Articles</h2>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: '20px',
                                    marginTop: '20px'
                                }}>
                                    {relatedArticles.map(related => (
                                        <div
                                            key={related._id}
                                            onClick={() => navigate(`/article/${related.slug || related._id}`)}
                                            style={{
                                                background: '#fff',
                                                borderRadius: '12px',
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-4px)';
                                                e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.12)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                                            }}
                                        >
                                            <div style={{ position: 'relative', aspectRatio: '16/9' }}>
                                                <img
                                                    src={related.img || related.imgData || 'https://via.placeholder.com/400x225'}
                                                    alt={related.title}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    onError={(e) => e.target.src = 'https://via.placeholder.com/400x225?text=No+Image'}
                                                />
                                            </div>
                                            <div style={{ padding: '12px' }}>
                                                <h4 style={{
                                                    fontSize: '14px',
                                                    marginBottom: '6px',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}>
                                                    {related.title}
                                                </h4>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    fontSize: '11px',
                                                    color: '#888'
                                                }}>
                                                    <span>{related.views?.toLocaleString() || 0} views</span>
                                                    <span>{formatDate(related.date)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="sidebar-box" style={{ background: '#fff', border: '1px solid var(--lgray)', borderRadius: '3px', padding: '16px', marginBottom: '20px' }}>
                            <div className="sb-title" style={{ fontFamily: 'var(--ff-label)', fontSize: '17px', letterSpacing: '1.5px', color: '#fff', background: 'var(--navy)', margin: '-16px -16px 14px', padding: '10px 14px' }}>
                                🔥 Trending Now
                            </div>
                            {articles.slice(0, 6).map((art, idx) => (
                                <div
                                    key={art._id}
                                    className="trending-item"
                                    style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--lgray)', cursor: 'pointer' }}
                                    onClick={() => navigate(`/article/${art.slug || art._id}`)}
                                >
                                    <div className="num" style={{ fontFamily: 'var(--ff-label)', fontSize: '22px', color: 'var(--red)', lineHeight: 1, width: '28px', flexShrink: 0 }}>
                                        {idx + 1}
                                    </div>
                                    <div>
                                        <h6 style={{ fontSize: '13px', lineHeight: '1.4', fontWeight: '600' }}>{art.title}</h6>
                                        <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                                            👁️ {art.views?.toLocaleString() || 0} views
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ArticlePage;